import { LocationVarKey } from "../Generated/LocationVarKey.Data";
import type { CortexCatalog, CortexCategory, CortexTopic } from "./Cortex.Types";
import { DISCOUNTS } from "./CortexDiscounts.Data";
import { FACILITY_AND_SPACE_FEATURES } from "./CortexFacilityFeatures.Data";
import { FACILITY_INFO } from "./CortexFacilityInfo.Data";
import { URLS, findURLDef } from "./CortexURLs.Data";
import { allFeatureVarKeys } from "./FacilityAndSpaceFeatures.Helpers";

// ─── Categories ──────────────────────────────────────────────────────────

const CATEGORIES: CortexCategory[] = [
  { id: "account-management", label: "Account & Billing", description: "How the bot handles accounts, payments, billing, and account-holder data." },
  {
    id: "customer-lifecycle",
    label: "Customer Lifecycle",
    description: "Reservation, referral, move-in logistics, move-out, and lease changes - the customer's journey through the facility."
  },
  {
    id: "lease-and-pricing",
    label: "Lease & Pricing",
    description: "Lease terms, rental requirements, insurance, storage policies, inspections, and pricing/fee policy."
  },
  {
    id: "facility-operations",
    label: "Facility Operations",
    description: "How the facility runs - security, gate access, auctions, vehicle storage, mail, pest control, dumpster/disposal."
  },
  {
    id: "sensitive-situations",
    label: "Sensitive Situations",
    description: "When and how the bot hands off in sensitive conversations: profanity, accessibility/language, damage and theft claims."
  }
];

// ─── Topics ──────────────────────────────────────────────────────────────
//
// Only typed-settings topics remain. The 25 prose-policy topics (and every
// prose field: policyVarKey / defaultSource / formattingInstructions /
// presets / derivatives) were removed with the Policy Builder cutover —
// operator policies are structured worksheets now (libs/policy-definitions,
// served by PolicyService). See
// docs/snowy/plans/2026-07-15-policy-builder-bot-cutover.md §6.2.

// Internal-shape: catalog literals declare sections + optional varKeys.
// `TOPICS_WITH_VARKEYS` below stamps the final `varKeys` field for each topic.
type CatalogTopicLiteral = Omit<CortexTopic, "varKeys"> & { varKeys?: LocationVarKey[] };

const TOPICS: CatalogTopicLiteral[] = [
  {
    id: "profanity-handling",
    categoryId: "sensitive-situations",
    label: "Profanity Handling",
    description: "What the bot does when a visitor uses profanity.",
    sections: [
      {
        id: "default",
        label: "Profanity Handling",
        settings: [
          {
            kind: "select",
            key: "onProfanity",
            varKey: LocationVarKey.CortexProfanityHandlingOnProfanity,
            label: "On profanity",
            description: "How the bot handles profanity from a visitor.",
            options: [
              { value: "transfer-to-human", label: "Transfer to human" },
              { value: "capture-feedback", label: "Capture feedback" }
            ],
            default: "transfer-to-human"
          }
        ]
      }
    ]
  },

  {
    // ENG-3390 (Chuck, 2026-09-28) — one scope-inherited setting for every
    // routing group; the per-group column it replaces is gone.
    id: "escalation-pushback",
    categoryId: "sensitive-situations",
    label: "Before Connecting a Caller",
    description: "Offer to help once before connecting a caller to the team.",
    sections: [
      {
        id: "default",
        label: "Before Connecting a Caller",
        settings: [
          {
            kind: "select",
            key: "escalationPushbackMode",
            varKey: LocationVarKey.CortexEscalationPushbackMode,
            label: "Before connecting a caller to the team",
            description: "Applies to every group. Frustrated callers, emergencies and sensitive situations always connect right away.",
            options: [
              { value: "connect-right-away", label: "Connect right away" },
              { value: "offer-help-once", label: "Offer to help once" },
              { value: "offer-help-once-unless-frustrated", label: "Offer to help once unless the customer is frustrated" }
            ],
            default: "offer-help-once-unless-frustrated"
          }
        ]
      }
    ]
  },

  // Structured-var topics - no sections, just toggles + inputs.
  // The auto-computed varKeys walk would yield [] for these; we declare
  // varKeys explicitly so the server can gate saves correctly.
  {
    id: "facility-info",
    categoryId: "facility-operations",
    label: "Facility Info",
    description: "Basic facility identity, contact, and localization data.",
    sections: [],
    // Appends currency/length-unit vars, plus CortexFacilityFMSSyncEnabled (ENG-3388), CortexPushConversationSummaryToFms
    // (ENG-3489) and TwilioAppId (plain, inline vars).
    varKeys: [
      ...FACILITY_INFO.map((f) => f.cortexVarKey),
      LocationVarKey.CortexCurrencyCode,
      LocationVarKey.CortexLengthUnit,
      LocationVarKey.CortexFacilityFMSSyncEnabled,
      LocationVarKey.CortexPushConversationSummaryToFms,
      LocationVarKey.TwilioAppId
    ]
  },
  {
    id: "amenities-and-services",
    categoryId: "facility-operations",
    label: "Amenities & Services",
    description: "Toggle the amenities, services, and storage types this facility offers.",
    sections: [],
    // Up to three plain vars per feature: on/off (switches), display name (all), wording (switches).
    // Paired features also need their URL var, for the row's hint.
    varKeys: [...allFeatureVarKeys(), ...FACILITY_AND_SPACE_FEATURES.flatMap((f) => (f.pairedUrlKey ? [findURLDef(f.pairedUrlKey)!.urlVarKey] : []))]
  },
  {
    id: "urls",
    categoryId: "facility-operations",
    label: "URLs",
    description: "External URLs the bot may share - pay-online, account login, sizing guide, etc.",
    sections: [],
    varKeys: URLS.flatMap((l) => [l.urlVarKey, l.enabledVarKey])
  },
  {
    id: "discounts",
    categoryId: "facility-operations",
    label: "Discounts",
    description:
      "Author per-discount details to enable each discount, and override display labels. An empty details field means the discount is NOT offered.",
    sections: [],
    varKeys: [...DISCOUNTS.map((d) => d.detailsVarKey), ...DISCOUNTS.filter((d) => !!d.nameVarKey).map((d) => d.nameVarKey as LocationVarKey)]
  },

  // ENG-3138 — the identifiers the location URL service builds links from, and the operator's own
  // "how to finish your rental" message. Snowy reads only `Cortex*` variables, so these need an
  // editor of their own; without one an operator would edit the legacy fields and drift away from
  // what the bot actually reads.
  {
    id: "website",
    categoryId: "facility-operations",
    label: "Website",
    description: "How the bot builds links into this facility's own website and its spot.is pages.",
    sections: [
      {
        id: "website",
        label: "Website",
        settings: [
          {
            kind: "select",
            key: "websiteProvider",
            varKey: LocationVarKey.CortexWebsiteProvider,
            label: "Website Provider",
            description: "Who builds this facility's website. Decides which rental-form link the bot sends a customer to.",
            options: [
              { value: "", label: "Not set" },
              { value: "none", label: "None" },
              { value: "golocal", label: "GoLocal" },
              { value: "g5", label: "G5" },
              { value: "storagepug", label: "StoragePug" },
              { value: "storedge", label: "StorEdge" },
              { value: "mariposa", label: "Mariposa" },
              { value: "automatit", label: "Automatit" },
              { value: "storagely", label: "Storagely" }
            ],
            default: ""
          },
          {
            kind: "text",
            key: "urlLookupPattern",
            varKey: LocationVarKey.CortexURLLookupPattern,
            label: "Website URL Lookup Pattern",
            description: "Path appended to the facility website to reach this location's own page.",
            placeholder: "/storage-locations/louisiana/addis",
            default: ""
          },
          {
            kind: "text",
            key: "storagelyClientId",
            varKey: LocationVarKey.CortexStoragelyClientID,
            label: "Storagely Client ID",
            description: "Used in the Storagely rental-form redirect. Without it the bot sends customers to the facility website instead.",
            placeholder: "4821",
            default: "",
            showIf: { key: "websiteProvider", equals: "storagely" }
          },
          {
            kind: "text",
            key: "storagePugBaseUrl",
            varKey: LocationVarKey.CortexStoragePugSingleLocationBaseURL,
            label: "StoragePug Single-Location Base URL",
            description: "For single-location StoragePug sites. Leave blank and the facility website's address is used.",
            placeholder: "https://storage.example.com",
            default: "",
            showIf: { key: "websiteProvider", equals: "storagepug" }
          }
        ]
      }
    ]
  },
  {
    id: "gate-code",
    categoryId: "facility-operations",
    label: "Gate Code",
    description: "Per-facility keypad instructions the bot appends when delivering a gate code.",
    sections: [
      {
        id: "default",
        label: "Gate Code",
        settings: [
          {
            kind: "text",
            key: "gateCodeInstructions",
            varKey: LocationVarKey.CortexFacilityGateCodeInstructions,
            label: "Gate code keypad instructions",
            description:
              "Per-facility keypad instructions appended whenever the bot delivers a gate code (e.g. 'Press # then your code'). Leave blank to deliver only the code.",
            placeholder: "Press # then your code",
            default: ""
          }
        ]
      }
    ]
  },

  {
    // ENG-3357 — Swivl-staff custom instructions (Policies tab, bottom of the
    // list, staff-only). One bounded free-text var rendered as its own
    // system-prompt section after the platform rules. Studio adds the Policy
    // Assistant drafting panel on top of this setting.
    id: "custom-instructions",
    categoryId: "customer-lifecycle",
    label: "Custom instructions",
    description: "Swivl-staff free-form instructions for the bot at this scope — conduct only, for one-off needs no structured setting covers.",
    sections: [
      {
        id: "default",
        label: "Custom instructions",
        settings: [
          {
            kind: "textarea",
            key: "customInstructions",
            varKey: LocationVarKey.CortexCustomInstructions,
            label: "Custom instructions",
            description:
              "Free-form instructions for the bot at this scope — conduct only. Facts (hours, fees, amenities) belong on their own pages; escalation and ticket rules live on Escalation & Ticketing. Platform rules on truthfulness and tool outcomes always win. Max 1,500 characters.",
            placeholder:
              "e.g. When a customer mentions they are a current tenant of our sister brand Sunny Storage, welcome them as an existing customer.",
            rows: 8,
            default: ""
          }
        ]
      }
    ]
  },
  {
    id: "lead-creation",
    categoryId: "customer-lifecycle",
    label: "Lead Creation",
    description: "When the bot's sales-tagged hand-offs auto-create a CRM/FMS lead.",
    sections: [
      {
        id: "default",
        label: "Lead Creation",
        settings: [
          {
            kind: "toggle",
            key: "createLeadForMovingTrucks",
            varKey: LocationVarKey.CortexCreateLeadForMovingTrucks,
            label: "Create leads for moving-truck-only inquiries",
            description:
              "When ON, a visitor whose only interest is renting a moving truck (U-Haul etc.) still creates a CRM/FMS lead on hand-off. Turn OFF if truck rentals are referred out (e.g. a U-Haul deal) — the team ticket is still created, only the lead is skipped.",
            default: true
          }
        ]
      }
    ]
  },

  {
    id: "move-out",
    categoryId: "customer-lifecycle",
    label: "Move-out",
    description: "The customer-facing move-out request form the bot can text or email to a tenant.",
    sections: [
      {
        id: "default",
        label: "Move-out",
        settings: [
          {
            kind: "text",
            key: "moveOutFormURL",
            varKey: LocationVarKey.CortexMoveOutFormURL,
            label: "Move Out Form URL",
            description:
              "Customer-facing move out request form. When set, the bot can offer to text or email this link to a tenant who wants to move out.",
            placeholder: "https://example.com/move-out",
            default: ""
          }
        ]
      }
    ]
  },

  {
    id: "welcome-messages",
    categoryId: "customer-lifecycle",
    label: "Welcome Messages",
    description:
      "Your own opening line per kind of arriving person — known customer, returning user, new user — per channel. Replaces the bot's usual greeting when on; a primed conversation (Conversation Starter / CTA) never shows one.",
    sections: [
      {
        id: "default",
        label: "Welcome Messages",
        settings: [
          {
            kind: "toggle",
            key: "welcomeMessagesEnabled",
            varKey: LocationVarKey.CortexWelcomeMessagesEnabled,
            label: "Use custom welcome messages",
            description:
              "When ON, the messages below open the conversation exactly as written. Cells left empty fall back to the bot's normal greeting.",
            default: false
          },
          {
            kind: "variable-textarea",
            key: "welcomeMessageKnownCustomerWeb",
            varKey: LocationVarKey.CortexWelcomeMessageKnownCustomerWeb,
            label: "Known customer — web chat",
            description: "Sent on web chat to a known customer (someone with an active lease).",
            placeholder: "Not set — the bot's normal greeting applies.",
            default: ""
          },
          {
            kind: "variable-textarea",
            key: "welcomeMessageKnownCustomerSms",
            varKey: LocationVarKey.CortexWelcomeMessageKnownCustomerSms,
            label: "Known customer — SMS",
            description: "Sent on SMS to a known customer (someone with an active lease).",
            placeholder: "Not set — the bot's normal greeting applies.",
            default: ""
          },
          {
            kind: "variable-textarea",
            key: "welcomeMessageKnownCustomerVoice",
            varKey: LocationVarKey.CortexWelcomeMessageKnownCustomerVoice,
            label: "Known customer — voice",
            description: "Sent on voice to a known customer (someone with an active lease).",
            placeholder: "Not set — the bot's normal greeting applies.",
            default: ""
          },
          {
            kind: "variable-textarea",
            key: "welcomeMessageReturningUserWeb",
            varKey: LocationVarKey.CortexWelcomeMessageReturningUserWeb,
            label: "Returning user — web chat",
            description: "Sent on web chat to a returning user (someone who has talked to us before but has no active lease).",
            placeholder: "Not set — the bot's normal greeting applies.",
            default: ""
          },
          {
            kind: "variable-textarea",
            key: "welcomeMessageReturningUserSms",
            varKey: LocationVarKey.CortexWelcomeMessageReturningUserSms,
            label: "Returning user — SMS",
            description: "Sent on SMS to a returning user (someone who has talked to us before but has no active lease).",
            placeholder: "Not set — the bot's normal greeting applies.",
            default: ""
          },
          {
            kind: "variable-textarea",
            key: "welcomeMessageReturningUserVoice",
            varKey: LocationVarKey.CortexWelcomeMessageReturningUserVoice,
            label: "Returning user — voice",
            description: "Sent on voice to a returning user (someone who has talked to us before but has no active lease).",
            placeholder: "Not set — the bot's normal greeting applies.",
            default: ""
          },
          {
            kind: "variable-textarea",
            key: "welcomeMessageUnknownWeb",
            varKey: LocationVarKey.CortexWelcomeMessageUnknownWeb,
            label: "Unknown — web chat",
            description: "Sent on web chat to an unknown person (someone we have no records of).",
            placeholder: "Not set — the bot's normal greeting applies.",
            default: ""
          },
          {
            kind: "variable-textarea",
            key: "welcomeMessageUnknownSms",
            varKey: LocationVarKey.CortexWelcomeMessageUnknownSms,
            label: "Unknown — SMS",
            description: "Sent on SMS to an unknown person (someone we have no records of).",
            placeholder: "Not set — the bot's normal greeting applies.",
            default: ""
          },
          {
            kind: "variable-textarea",
            key: "welcomeMessageUnknownVoice",
            varKey: LocationVarKey.CortexWelcomeMessageUnknownVoice,
            label: "Unknown — voice",
            description: "Sent on voice to an unknown person (someone we have no records of).",
            placeholder: "Not set — the bot's normal greeting applies.",
            default: ""
          }
        ]
      }
    ]
  }

  // pricing-policies topic retired 2026-07-17: its fee scalars moved into the
  // policy worksheets (opt-in amount fields) and its discount varKeys were a
  // duplicate of the `discounts` topic above.
];

// ─── Exports ─────────────────────────────────────────────────────────────

// Auto-attach `varKeys` to every topic so server-side gating + new lookup can use it.
// Topics that declare `varKeys` explicitly (structured-var topics like facility-info,
// or topics that extend their settings with extra structured vars like pricing-policies)
// win over the auto-computed section walk.
const TOPICS_WITH_VARKEYS: CortexTopic[] = TOPICS.map((t) => ({
  ...t,
  varKeys: t.varKeys ?? [...(t.sections ?? []).flatMap((s) => s.settings.map((set) => set.varKey))]
}));

export const STORAGE_CORTEX_CATALOG: CortexCatalog = {
  categories: CATEGORIES,
  topics: TOPICS_WITH_VARKEYS
};

/** Flat var-key → topic lookup. Returns null for non-Cortex keys. */
export function findCortexTopicByVarKey(varKey: string): { topicId: string } | null {
  for (const topic of TOPICS_WITH_VARKEYS) {
    if (!topic.varKeys?.includes(varKey as LocationVarKey)) continue;
    return { topicId: topic.id };
  }
  return null;
}

/**
 * Cortex variables only Swivl staff may write, mirrored from the Studio pages that
 * carry them: the Details v3 "Website" topic (`website`), the "Admin" page
 * (`Admin.Page.tsx` PAGE_KEYS) and the Custom Instructions section (ENG-3357).
 * `POST /cortex/save-raw` is member-scoped (ENG-3491); the server refuses these
 * keys for non-staff so the UI gate cannot be bypassed through the API.
 */
export const STAFF_ONLY_CORTEX_VAR_KEYS: ReadonlySet<LocationVarKey> = new Set<LocationVarKey>([
  ...(TOPICS_WITH_VARKEYS.find((t) => t.id === "website")?.varKeys ?? []),
  LocationVarKey.CortexVoicePreWelcomeMessage,
  LocationVarKey.CortexIsSMSCampaignsEnabled,
  LocationVarKey.CortexCustomInstructions
]);

export function isStaffOnlyCortexVarKey(key: LocationVarKey): boolean {
  return STAFF_ONLY_CORTEX_VAR_KEYS.has(key);
}
